import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'

import type { Trace } from '@engine/models'

import { Table } from '#components/Table'
import { formatDateTime, formatDuration, getDurationColor } from '#utils/format'

import { getTraceDuration, getTraceError } from './traceUtils'

interface TracesTableProps {
  traces: Trace[]
  onSelect: (trace: Trace) => void
  selectedTraceId?: string
  infiniteScrollRef?: React.RefObject<HTMLDivElement>
  isFetchingMore?: boolean
}

const columnHelper = createColumnHelper<Trace>()

export const TracesTable = ({
  traces,
  onSelect,
  selectedTraceId,
  infiniteScrollRef,
  isFetchingMore,
}: TracesTableProps) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor('timeStamp', {
        header: 'Timestamp',
        cell: info => (
          <div className="align-top">
            <div>{formatDateTime(info.getValue())}</div>
          </div>
        ),
      }),
      columnHelper.accessor('category', {
        header: 'Layer',
        cell: info => <span className="font-semibold">{info.getValue()}</span>,
      }),
      columnHelper.accessor('action', {
        header: 'Action',
        cell: info => (
          <span className="font-mono text-sm">{info.getValue() || '-'}</span>
        ),
      }),
      columnHelper.display({
        id: 'carrier',
        header: 'Carrier',
        cell: info =>
          info.row.original.carrier?.code ? (
            <span className="badge badge-sm badge-info uppercase">
              {info.row.original.carrier?.code}
            </span>
          ) : (
            <span className="badge badge-sm">-</span>
          ),
      }),
      columnHelper.accessor('targetId', {
        header: 'Target ID',
        cell: info => (
          <span className="font-mono text-sm">{info.getValue() || '-'}</span>
        ),
      }),
      columnHelper.display({
        id: 'duration',
        header: 'Duration',
        cell: info => {
          const duration = getTraceDuration(info.row.original)
          return (
            <span className={`font-mono ${getDurationColor(duration)}`}>
              {duration === undefined ? '-' : formatDuration(duration)}
            </span>
          )
        },
      }),
      columnHelper.display({
        id: 'error',
        header: 'Error',
        cell: info => {
          const error = getTraceError(info.row.original)?.error
          return error ? (
            <span
              className="text-error text-sm line-clamp-2 max-w-md"
              title={error}
            >
              {error}
            </span>
          ) : (
            <span className="badge badge-sm badge-success">OK</span>
          )
        },
      }),
    ],
    [],
  )

  return (
    <Table
      data={traces}
      columns={columns}
      getRowId={trace => trace.traceId}
      onRowClick={onSelect}
      selectedId={selectedTraceId}
      infiniteScrollRef={infiniteScrollRef}
      isFetchingMore={isFetchingMore}
    />
  )
}
